import * as React from 'react';
import * as styles from './ContentCreatorItem.css';
import { IAIcon } from '../../icon/Icon';
import DateTimeService from '../../../services/DateTimeService';
import { IMyContentItem } from '../../../interfaces/IMyContentItem';
import { IATooltip } from '../../tooltip/Tooltip';
import { LocalizationService } from '../../../services/LocalizationService';
import "../../../styles/BasicIA.css";
import { IFontSlots } from '../../../interfaces/IFontSlots';
import { RecurrenceHelper } from '../../../RecurrenceHelper';

export interface IProps {
  highlightColor?: string;
  showSkeleton?: boolean;
  isMobile?: boolean;
  fontSlots: IFontSlots;
  isCurrentUserApprover?: boolean;
  contentItem?: IMyContentItem;
  onClickEdit?: (content: IMyContentItem) => void;
  onClickOpen?: (content: IMyContentItem) => void;
  onClickDelete?: (content: IMyContentItem) => void;
  onClickReview?: (content: IMyContentItem) => void;
}

export interface IState {
  commandaBarOpen: boolean;
}

export class ContentCreatorItem extends React.Component<IProps, IState> {
  private readonly localizationService: LocalizationService = new LocalizationService();
  private readonly dateTimeService: DateTimeService = new DateTimeService();

  constructor(props: IProps) {
    super(props);
    this.state = {
      commandaBarOpen: false
    };
  }

  private getItemColorAndText(): { color: string; text: string, icon: string } {
    let statusColor = "green";
    let statusIconUrl = "https://intraactivestorage.blob.core.windows.net/cdn/icons/Checkmark%20Circle/SVG/ic_fluent_checkmark_circle_48_filled.svg";
    let statusText = this.localizationService.strings.ContentCreator_Active;
    if (this.props.contentItem && this.props.contentItem.endDate && new Date(this.props.contentItem.endDate).valueOf() < new Date().valueOf()) {
      statusColor = "red";
      statusIconUrl = "https://intraactivestorage.blob.core.windows.net/cdn/icons/Clock/SVG/ic_fluent_clock_48_filled.svg";
      statusText = this.localizationService.strings.ContentCreator_Expired;
    }
    if (this.props.contentItem && this.props.contentItem.startDate && new Date(this.props.contentItem.startDate).valueOf() > new Date().valueOf()) {
      statusColor = "orange";
      statusIconUrl = "https://intraactivestorage.blob.core.windows.net/cdn/icons/Clock/SVG/ic_fluent_clock_48_filled.svg";
      statusText = this.localizationService.strings.ContentCreator_Scheduled;
    }
    if (this.props.contentItem && this.props.contentItem.draft) {
      statusColor = "#777777";
      statusIconUrl = "https://intraactivestorage.blob.core.windows.net/cdn/icons/Circle%20Edit/SVG/ic_fluent_circle_edit_24_filled.svg";
      statusText = this.localizationService.strings.ContentCreator_Draft;
    }
    if (this.props.contentItem && this.props.contentItem.approvalStatus === "Pending") {
      statusColor = "#777777";
      statusIconUrl = "https://intraactivestorage.blob.core.windows.net/cdn/icons/Approvals%20App/SVG/ic_fluent_approvals_app_32_regular.svg";
      statusText = this.localizationService.strings.ContentCreator_ApprovalStatusPending;
    }
    if (this.props.contentItem && this.props.contentItem.approvalStatus === "Rejected") {
      statusColor = "red";
      statusIconUrl = "https://intraactivestorage.blob.core.windows.net/cdn/icons/Dismiss%20Circle/SVG/ic_fluent_dismiss_circle_48_filled.svg";
      statusText = this.localizationService.strings.ContentCreator_ApprovalStatusRejected;
    }

    if (this.props.contentItem?.recurrence && this.props.contentItem?.recurrenceData) {
      // const data = this.props.contentItem.recurrenceData;
      const recurrenceEndDate = this.props.contentItem.recurrenceEndDate;
      statusIconUrl = "https://intraactivestorage.blob.core.windows.net/cdn/icons/Arrow%20Repeat%20All/SVG/ic_fluent_arrow_repeat_all_24_regular.svg";
      if (recurrenceEndDate && new Date(recurrenceEndDate) < new Date()) {
        statusColor = "red";
        statusText = this.localizationService.strings.ContentCreator_Expired;
      } else {
        statusColor = "green";
        statusText = this.localizationService.strings.ContentCreator_Active;
      }
    }

    return { color: statusColor, text: statusText, icon: statusIconUrl };
  }

  private getItemPopup(): JSX.Element {
    const { color: statusColor, text: statusText } = this.getItemColorAndText();

    if (this.props.contentItem?.recurrence && this.props.contentItem?.recurrenceData) {
      const data = this.props.contentItem.recurrenceData;
      const intervalString = {
        "daily": this.localizationService.strings.Events_RecurrenceDays,
        "weekly": this.localizationService.strings.Events_RecurrenceWeeks,
        "monthly": this.localizationService.strings.Events_RecurrenceMonths,
        "yearly": this.localizationService.strings.Events_RecurrenceYears
      };
      
      return (
        <div className={styles.IA_contentCreatorItemPopupWrapper}>
          <div>{this.localizationService.strings.ContentCreator_RecurrenceStatus}: <span style={{ color: statusColor }} className={styles.IA_contentCreatorItemPopupText}>{statusText}</span></div>
          <div>{this.localizationService.strings.ContentCreator_FirstOccurrence}: <span className={styles.IA_contentCreatorItemPopupText}>{this.dateTimeService.ConvertToDDMMYYYY(this.props.contentItem.startDate)}</span></div>
          <div>{this.localizationService.strings.ContentCreator_LastOccurrence}: <span className={styles.IA_contentCreatorItemPopupText}>{this.props.contentItem.recurrenceEndDate ? this.dateTimeService.ConvertToDDMMYYYY(this.props.contentItem.recurrenceEndDate) : this.localizationService.strings.ContentCreator_NoEndDate}</span></div>
          <div>{`${this.localizationService.strings.ContentCreator_RepeatEvery} ${data.interval} ${intervalString[data.recurrenceType]}`}</div>
          {data.recurrenceType === "daily" &&
            <div>{this.localizationService.strings.ContentCreator_EveryDay}</div>
          }
          {data.recurrenceType === "weekly" &&
            <div style={{ flexWrap: "wrap" }}>{data.daysOfWeek?.map((day) => { 
                return <span className={styles.IA_contentCreatorItemPopupWeeklyDay}>{RecurrenceHelper.getLocalizedDay(day, this.localizationService)}</span>
              })}
            </div>
          }
          {data.recurrenceType === "monthly" &&
            <div>{RecurrenceHelper.getMonthlyRecurrencePatternTypes(this.props.contentItem.startDate, this.localizationService).find(x => x.monthlyPattern === data.monthlyPattern).text}</div>
          }
          {data.recurrenceType === "yearly" &&
            <div>{this.localizationService.strings.ContentCreator_EveryYear + " " + this.dateTimeService.ConvertToDDMM(this.props.contentItem.startDate)}</div>
          }
        </div>
      )
    }

    return (
      <div className={styles.IA_contentCreatorItemPopupWrapper}>
        <div>{this.localizationService.strings.ContentCreator_Status}: <span style={{ color: statusColor }} className={styles.IA_contentCreatorItemPopupText}>{statusText}</span></div>
        <div>{this.localizationService.strings.Messages_FilterPanel_StartTime}: <span className={styles.IA_contentCreatorItemPopupText}>{this.dateTimeService.ConvertToDDMMYYYYHHMM(this.props.contentItem.startDate)}</span></div>
        <div>{this.localizationService.strings.Messages_FilterPanel_EndTime}: <span className={styles.IA_contentCreatorItemPopupText}>{this.props.contentItem.endDate ? this.dateTimeService.ConvertToDDMMYYYYHHMM(this.props.contentItem.endDate) : "-"}</span></div>
      </div>
    );
  }

  public render(): JSX.Element {
    let commandBarItems: Array<any> = [];
    if (!this.props.showSkeleton) {
      if (this.props.contentItem.site !== "IntraActive-ContentOrganizer") {
        commandBarItems.push(
          <div
            className={styles.IA_commandBarItem}
            key={`commandbar-edit-${this.props.contentItem.uniqueId}`}
            onClick={() => this.props.onClickEdit(this.props.contentItem)}
          >
            <IAIcon
              title="Edit"
              size={14}
              color={this.props.highlightColor}
              style={{
                float: "left"
              }}
            />
            <div
              style={{
                color: "#333333",
                float: "left",
                marginLeft: 7
              }}
            >
              {this.localizationService.strings.Card_CommandBar_Edit}
            </div>
          </div>
        );
      }
      if (!this.props.isCurrentUserApprover) {
        commandBarItems.push(
          <div
            className={styles.IA_commandBarItem}
            key={`commandbar-open-${this.props.contentItem.uniqueId}`}
            onClick={() => this.props.onClickOpen(this.props.contentItem)}
          >
            <IAIcon
              title="Open"
              size={14}
              color={this.props.highlightColor}
              style={{
                float: "left"
              }}
            />
            <div
              style={{
                color: "#333333",
                float: "left",
                marginLeft: 7
              }}
            >
              {this.localizationService.strings.Card_CommandBar_Open}
            </div>
          </div>
        );
      }
      if (!this.props.isCurrentUserApprover) {
        commandBarItems.push(
          <div
            className={styles.IA_commandBarItem}
            key={`commandbar-delete-${this.props.contentItem.uniqueId}`}
            onClick={() => this.props.onClickDelete(this.props.contentItem)}
          >
            <IAIcon
              title="Close"
              size={14}
              color={this.props.highlightColor}
              style={{
                float: "left"
              }}
            />
            <div
              style={{
                color: "#333333",
                float: "left",
                marginLeft: 7
              }}
            >
              {this.localizationService.strings.Card_CommandBar_Delete}
            </div>
          </div>
        );
      } else {
        commandBarItems.push(
          <div
            className={styles.IA_commandBarItem}
            key={`commandbar-review-${this.props.contentItem.uniqueId}`}
            onClick={() => this.props.onClickReview(this.props.contentItem)}
          >
            <IAIcon
              title="Preview"
              size={14}
              color={this.props.highlightColor}
              style={{
                float: "left"
              }}
            />
            <div
              style={{
                color: "#333333",
                float: "left",
                marginLeft: 7
              }}
            >
              {this.localizationService.strings.Card_CommandBar_Review}
            </div>
          </div>
        );
      }
    }

    const { color: statusColor, icon: statusIconUrl } = this.getItemColorAndText();
    
    return (
      <div
        className="IA_OnFocusOutline_outer"
        onClick={() => this.props.onClickOpen(this.props.contentItem)}
        onKeyPress={(event) => {
          if (event.key === "Enter") {
            this.props.onClickOpen(this.props.contentItem);
          }
        }}
        tabIndex={0}
      >
        <div
          className={[styles.IA_contentCreatorItem, "IA_OnFocusOutline_inner"].join(" ")}
          tabIndex={-1}
        >
          <div
            onClick={(e) => {
              e.stopPropagation();
              this.setState({ commandaBarOpen: !this.state.commandaBarOpen })
            }}
          >
            <div className={styles.IA_cardBarBtn}>
              <IAIcon
                title="Show Action"
                size={18}
                color={this.props.highlightColor}
              />
            </div>
            {this.state.commandaBarOpen &&
              <ul className={styles.IA_cardBarItems}>
                {commandBarItems}
              </ul>
            }
          </div>
          <div className={styles.IA_icon}>
            <IAIcon
              title={this.props.contentItem.site.toLocaleLowerCase() === "intraactive-messages" ? "Comment" : this.props.contentItem.site.toLocaleLowerCase() === "intraactive-events" ? "Calendar" : "Content Organizer"}
              size={22}
              color={this.props.highlightColor}
            />
            <IATooltip
              content="Tooltip"
              type={this.props.isMobile ? "popup" : "bubble"}
              styles={{
                width: 205,
                textAlign: "left",
                color: "#333333",
                marginTop: -15,
                marginLeft: 10
              }}
              componentContent={this.getItemPopup()}
              borderRadius={5}
            >
              <div
                style={{
                  width: 10,
                  height: 10,
                  borderRadius: 10,
                  position: "absolute",
                  top: 28,
                  left: 28,
                  backgroundColor: "#ffffff"
                }}
              >
                <IAIcon
                  url={statusIconUrl}
                  size={11}
                  color={statusColor}
                  style={{
                    marginTop: -3,
                  }}
                />
              </div>
            </IATooltip>
          </div>
          <div
            className={styles.IA_title}
            style={{
              fontFamily: this.props.fontSlots?.heading?.fontFamily
            }}
          >
            {this.props.contentItem.title}
          </div>
          <div
            className={styles.IA_date}
            style={{
              fontFamily: this.props.fontSlots?.body?.fontFamily
            }}
          >
            {this.dateTimeService.ConvertToDDMMYYYYHHMM(this.props.contentItem.date)}
          </div>
        </div>
      </div>
    );
  }


}